import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Switch, 
  message, 
  Space, 
  Popconfirm,
  Tag,
  Typography,
  Divider,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ShieldOutlined,
  GlobalOutlined,
  UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { getApiBaseUrl } from '../../../config/apiBase';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

const API_BASE_URL = getApiBaseUrl();

const KeywordBlocking = () => {
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRules();
    fetchUsers();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/admin/global-settings/keyword-block`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRules(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch keyword rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/admin/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleSaveRule = async (values) => {
    try {
      const token = localStorage.getItem('token');
      console.log('Saving rule with values:', values);
      
      if (editingRule) {
        // For editing, update the single rule
        const response = await axios.put(
          `${API_BASE_URL}/admin/global-settings/keyword-block/${editingRule.id}`,
          {
            keyword: values.keywords.split(/[,\n\r]+/).map(k => k.trim()).filter(k => k)[0], // Take first keyword for single edit
            scope: values.scope,
            user_id: values.scope === 'GLOBAL' ? null : values.user_id,
            is_active: values.is_active
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Update response:', response.data);
        
        // Check for warnings about duplicates
        if (response.data.warning) {
          message.warning(response.data.warning);
        } else {
          message.success('Rule updated successfully');
        }
      } else {
        // Create new rules
        const response = await axios.post(
          `${API_BASE_URL}/admin/global-settings/keyword-block`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Save response:', response.data);
        
        // Check for warnings about duplicates
        if (response.data.warning) {
          message.warning(response.data.warning);
          if (response.data.duplicates && response.data.duplicates.length > 0) {
            message.info(`Skipped duplicates: ${response.data.duplicates.join(', ')}`);
          }
        } else {
          message.success('Rules created successfully');
        }
      }

      setModalVisible(false);
      setEditingRule(null);
      form.resetFields();
      fetchRules();
    } catch (error) {
      console.error('Save error:', error);
      if (error.response?.status === 409) {
        message.error(error.response.data.error || 'Duplicate keyword found');
      } else {
        message.error(error.response?.data?.error || 'Failed to save rule');
      }
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    form.setFieldsValue({
      keywords: rule.keyword, // Single keyword for edit
      scope: rule.scope,
      user_id: rule.user_id,
      is_active: rule.is_active
    });
    setModalVisible(true);
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/admin/global-settings/keyword-block/${ruleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Rule deleted successfully');
      fetchRules();
    } catch (error) {
      message.error('Failed to delete rule');
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/admin/global-settings/keyword-block/${ruleId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Rule status updated');
      fetchRules();
    } catch (error) {
      message.error('Failed to toggle rule');
    }
  };

  const columns = [
    {
      title: 'No',
      key: 'no',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Keyword',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (keyword) => <Text code>{keyword}</Text>,
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      width: 120,
      render: (scope) => (
        <Tag 
          icon={scope === 'GLOBAL' ? <GlobalOutlined /> : <UserOutlined />}
          color={scope === 'GLOBAL' ? 'red' : 'blue'}
        >
          {scope}
        </Tag>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 150,
      render: (userId) => {
        if (!userId) return '-';
        const user = users.find(u => u.id === userId);
        return user ? user.username : `User ${userId}`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditRule(record)}
          />
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleRule(record.id)}
          >
            {record.is_active ? 'Disable' : 'Enable'}
          </Button>
          <Popconfirm
            title="Are you sure to delete this rule?"
            onConfirm={() => handleDeleteRule(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Keyword Blocking</h1>
        <p className="text-gray-600">
          Manage keyword blocking rules to prevent SMS messages with specific content from being sent.
        </p>
      </div>

      <Alert
        message="Blocking Information"
        description="Global keywords apply to the entire system and cannot be overridden. User-specific keywords only apply to selected users."
        type="info"
        showIcon
        className="mb-6"
      />

      <Card>
        <div className="mb-4 flex justify-between items-center">
          <div>
            <Text strong>Total Rules: </Text>
            <Text>{rules.length}</Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingRule(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Add Keyword(s)
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rules`,
          }}
        />
      </Card>

      <Modal
        title={editingRule ? 'Edit Keyword Rule(s)' : 'Add Keyword Rule(s)'}
        open={modalVisible}
        destroyOnClose={true}
        onCancel={() => {
          setModalVisible(false);
          setEditingRule(null);
        }}
        afterClose={() => {
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          key={editingRule ? `edit-${editingRule.id}` : 'new'}
          form={form}
          layout="vertical"
          onFinish={handleSaveRule}
          initialValues={{
            scope: 'GLOBAL',
            is_active: true,
          }}
        >
          <Form.Item
            name="keywords"
            label={editingRule ? "Keyword" : "Keywords"}
            rules={[{ required: true, message: `Please enter ${editingRule ? 'a keyword' : 'keywords'}` }]}
            tooltip={editingRule ? "Edit the keyword" : "Enter multiple keywords separated by comma or new line"}
          >
            <TextArea
              rows={editingRule ? 1 : 4}
              placeholder={editingRule ? "Enter keyword" : "Enter keywords separated by comma or new line\nExample: OTP, FREE, WIN\nor\nOTP\nFREE\nWIN"}
            />
          </Form.Item>

          <Form.Item
            name="scope"
            label="Scope"
            rules={[{ required: true, message: 'Please select scope' }]}
          >
            <Select placeholder="Select scope">
              <Option value="GLOBAL">Global (System-wide)</Option>
              <Option value="USER">User-specific</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.scope !== currentValues.scope
            }
          >
            {({ getFieldValue }) => {
              const scope = getFieldValue('scope');
              // Reset user_id when scope changes
              if (scope !== 'USER') {
                form.setFieldsValue({ user_id: null });
              }
              
              if (scope === 'USER') {
                return (
                  <Form.Item
                    name="user_id"
                    label="User"
                    rules={[{ required: true, message: 'Please select a user' }]}
                  >
                    <Select 
                      placeholder="Select user"
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {users.map(user => (
                        <Option 
                          key={user.id} 
                          value={user.id}
                          label={`${user.username} (${user.email})`}
                        >
                          {user.username} ({user.email})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default KeywordBlocking;
